import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";

import * as auditLogsAction from "store/audit-logs/audit-logs.action";
import DefaultLayout from "layouts/default/default.layout";
import { ReportsListingFooter } from "components/pagination/listing-pagination.component";
import AuditLogsExportDropdown from "./audit-logs-export-dropdown.component";
import DateRangeFilter from "components/form/date-range-filter/date-range-filter.component";
import DropDown, { RADIO } from "components/form/dropdown/dropdown.component";
import Loading from "components/loading/loading.component";
import NoDataFound, { NoAuditLogs } from "components/no-data/no-data.component";
import notification from "helpers/notification/notification.helper";
import useDate from "hooks/useDate";
import { buildAuditLogsApiParams } from "helpers/audit-log/build-audit-logs-params.helper";
import { DATE_RANGE_SCOPES } from "store/date-range/date-range.action";
import {
  getAuditLogActionLabel,
  formatAuditLogTarget,
  getAuditLogChanges,
  hasExpandableAuditLogDetails,
  getAuditLogDetailsPreview
} from "helpers/audit-log/audit-log-labels.helper";

import "./audit-logs.styles.scss";

const getTooltipProps = value => {
  if (!value || value === "-" || value === "—") {
    return {};
  }
  return {
    "data-tip": value,
    "data-multiline": true
  };
};

const ACTION_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "login", label: "Login" },
  { value: "logout", label: "Logout" },
  { value: "two_factor_verify", label: "2FA Verified" },
  { value: "two_factor_enable", label: "2FA Enabled" },
  { value: "two_factor_disable", label: "2FA Disabled" },
  { value: "customer_create", label: "Customer Created" },
  { value: "customer_update", label: "Customer Updated" },
  { value: "customer_suspend", label: "Customer Suspended" },
  { value: "customer_unsuspend", label: "Customer Activated" },
  { value: "customer_delete", label: "Customer Deleted" },
  { value: "customer_export", label: "Customer Export" },
  {
    value: "customer_database_provision",
    label: "Customer Database Provisioned"
  },
  { value: "plan_update", label: "Plan Updated" },
  { value: "subscription_update", label: "Subscription Updated" },
  { value: "mc_user_create", label: "User Created" },
  { value: "mc_user_update", label: "User Updated" },
  { value: "tag_create", label: "Tag Created" },
  { value: "tag_delete", label: "Tag Deleted" }
];

const CATEGORY_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "auth", label: "Auth" },
  { value: "customer", label: "Customer" },
  { value: "plan", label: "Plan" },
  { value: "subscription", label: "Subscription" },
  { value: "user", label: "User" },
  { value: "export", label: "Export" },
  { value: "tag", label: "Tag" }
];

const SORTABLE_COLUMNS = [
  { key: "created_at", label: "Created At" },
  { key: "actor_name", label: "User Name" },
  { key: "actor_email", label: "Email" },
  { key: "action", label: "Action" },
  { key: "target", label: "Target" }
];

const AuditLogs = ({ location: { state } }) => {
  const dispatch = useDispatch();
  const moment = useDate();
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [parameters, setParameters] = useState({
    action: "",
    category: "",
    page: 1,
    limit: 25,
    sortBy: "created_at",
    sortOrder: "desc"
  });
  const [expandedRowIds, setExpandedRowIds] = useState([]);

  const auditLogsResponse = useSelector(
    state => state.auditLogs[auditLogsAction.GET_AUDIT_LOGS]
  );
  const isAuditLogsLoading = useSelector(
    state => state.auditLogs[auditLogsAction.AUDIT_LOGS_LOADING]
  );
  const isExportLoading = useSelector(
    state => state.auditLogs[auditLogsAction.EXPORT_AUDIT_LOGS_LOADING]
  );
  const exportAuditLogsResponse = useSelector(
    state => state.auditLogs[auditLogsAction.EXPORT_AUDIT_LOGS]
  );
  const exportAuditLogsErrors = useSelector(
    state => state.auditLogs[auditLogsAction.EXPORT_AUDIT_LOGS_ERRORS]
  );
  const { startDate, endDate } = useSelector(
    state => state.auditLogsDateRange
  );

  useEffect(() => {
    if (state) {
      setParameters(state);
    }
  }, [state]);

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }

    const queryParams = buildAuditLogsApiParams({
      ...parameters,
      startDate,
      endDate
    });
    dispatch(auditLogsAction.getAuditLogs(queryParams));
  }, [dispatch, parameters, startDate, endDate]);

  const logs = auditLogsResponse?.data || [];

  useEffect(() => {
    if (!isAuditLogsLoading) {
      ReactTooltip.rebuild();
    }
  }, [isAuditLogsLoading, logs, expandedRowIds, parameters.page]);

  useEffect(() => {
    if (!exportAuditLogsResponse?.response?.data) {
      return;
    }
    const { response, format = "xlsx" } = exportAuditLogsResponse;
    const extension = format === "csv" ? "csv" : "xlsx";
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `audit-logs-${new Date().toISOString().slice(0, 10)}.${extension}`
    );
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [exportAuditLogsResponse]);

  useEffect(() => {
    if (exportAuditLogsErrors) {
      notification.error(exportAuditLogsErrors, {
        position: "top-right"
      });
    }
  }, [exportAuditLogsErrors]);

  const toggleRow = logId => {
    setExpandedRowIds(prev =>
      prev.includes(logId)
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  const getFilterTitle = (options, value, fallback) =>
    options.find(option => option.value === value)?.label || fallback;

  const buildFilterOptions = (options, selectedValue) =>
    options.map(option => ({
      ...option,
      checked: option.value === selectedValue
    }));

  const handleSingleFilter = (options, field, label) => {
    const option = options.find(item => item.label === label);
    setParameters({
      ...parameters,
      page: 1,
      [field]: option?.value || ""
    });
    setOpenFilterDropdown(null);
  };

  const handleSort = columnKey => {
    setParameters(prev => {
      if (prev.sortBy === columnKey) {
        return {
          ...prev,
          page: 1,
          sortOrder: prev.sortOrder === "asc" ? "desc" : "asc"
        };
      }
      return {
        ...prev,
        page: 1,
        sortBy: columnKey,
        sortOrder: "asc"
      };
    });
  };

  const getSortIconClass = columnKey => {
    if (parameters.sortBy !== columnKey) {
      return "fa-sort";
    }
    return parameters.sortOrder === "asc" ? "fa-sort-up" : "fa-sort-down";
  };

  const handleExport = format => {
    dispatch(
      auditLogsAction.exportAuditLogs(
        buildAuditLogsApiParams({
          ...parameters,
          startDate,
          endDate
        }),
        format
      )
    );
  };

  const handleDateRangeApply = () => {
    setParameters(prev => ({
      ...prev,
      page: 1
    }));
  };

  return (
    <DefaultLayout>
      <div className="container">
        <div className="app__data--listing-header mb-20">
          <div className="app__data--listing-header-top">
            <div className="app__data--listing-header-left-col">
              <div className="app__data--listing-title">Audit Logs</div>
            </div>
          </div>
          <div className="app__data--listing-header-intro text-muted">
            Track sign-ins, sign-outs, and other activities in the Master Console.
          </div>
        </div>

        <div className="audit-logs__content">
          <div className="audit-logs__toolbar">
            <DateRangeFilter
              mode="redux"
              scope={DATE_RANGE_SCOPES.AUDIT_LOGS}
              onApply={handleDateRangeApply}
            />
            <AuditLogsExportDropdown
              isLoading={isExportLoading}
              onExport={handleExport}
            />
          </div>

          <div className="audit-logs__filters">
            <div className="audit-logs__filters-header">
              <i className="fas fa-filter"></i>
              <span>Filters</span>
            </div>

            <div className="audit-logs__filters-content">
              <div className="audit-logs__filter-item">
                <label className="audit-logs__filter-label">Action</label>
                <DropDown
                  title={getFilterTitle(
                    ACTION_FILTER_OPTIONS,
                    parameters.action,
                    "All"
                  )}
                  radioName="audit-logs-action"
                  options={buildFilterOptions(
                    ACTION_FILTER_OPTIONS,
                    parameters.action
                  )}
                  handleFilter={label =>
                    handleSingleFilter(ACTION_FILTER_OPTIONS, "action", label)
                  }
                  inputType={RADIO}
                  isOpen={openFilterDropdown === "action"}
                  onOpenChange={open =>
                    setOpenFilterDropdown(open ? "action" : null)
                  }
                />
              </div>

              <div className="audit-logs__filter-item">
                <label className="audit-logs__filter-label">Category</label>
                <DropDown
                  title={getFilterTitle(
                    CATEGORY_FILTER_OPTIONS,
                    parameters.category,
                    "All"
                  )}
                  radioName="audit-logs-category"
                  options={buildFilterOptions(
                    CATEGORY_FILTER_OPTIONS,
                    parameters.category
                  )}
                  handleFilter={label =>
                    handleSingleFilter(
                      CATEGORY_FILTER_OPTIONS,
                      "category",
                      label
                    )
                  }
                  inputType={RADIO}
                  isOpen={openFilterDropdown === "category"}
                  onOpenChange={open =>
                    setOpenFilterDropdown(open ? "category" : null)
                  }
                />
              </div>
            </div>
          </div>

          <Loading isLoading={isAuditLogsLoading} wrapperClassName="">
            {!isAuditLogsLoading && !logs.length ? (
              <NoDataFound
                title="No audit logs found"
                body="Try adjusting your filters or perform an action that generates logs."
                imageComponent={NoAuditLogs}
                wrapperClass="audit-logs__empty"
              />
            ) : (
              <div className="audit-logs__listing-card">
                <div className="audit-logs__table-wrapper">
                  <table className="table audit-logs__table">
                    <colgroup>
                      <col className="audit-logs__col audit-logs__col--toggle" />
                      <col className="audit-logs__col audit-logs__col--sno" />
                      <col className="audit-logs__col audit-logs__col--date" />
                      <col className="audit-logs__col audit-logs__col--user-name" />
                      <col className="audit-logs__col audit-logs__col--email" />
                      <col className="audit-logs__col audit-logs__col--action" />
                      <col className="audit-logs__col audit-logs__col--target" />
                      <col className="audit-logs__col audit-logs__col--details" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th></th>
                        <th>S.NO</th>
                        {SORTABLE_COLUMNS.map(column => (
                          <th
                            key={column.key}
                            aria-sort={
                              parameters.sortBy === column.key
                                ? parameters.sortOrder === "asc"
                                  ? "ascending"
                                  : "descending"
                                : "none"
                            }
                          >
                            <button
                              type="button"
                              className="audit-logs__sortable-th"
                              onClick={() => handleSort(column.key)}
                            >
                              <span className="audit-logs__sortable-th-label">
                                {column.label}
                              </span>
                              <span className="audit-logs__sortable-th-icon">
                                <i className={`fas ${getSortIconClass(column.key)}`}></i>
                              </span>
                            </button>
                          </th>
                        ))}
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, index) => {
                      const serialNo =
                        (parameters.page - 1) * parameters.limit + index + 1;
                      const target = formatAuditLogTarget(log);
                      const changes = getAuditLogChanges(log);
                      const detailsPreview = getAuditLogDetailsPreview(log);
                      const isExpandable = hasExpandableAuditLogDetails(log);
                      const isExpanded = expandedRowIds.includes(log.id);

                      return (
                        <React.Fragment key={log.id}>
                          <tr
                            className={[
                              "audit-logs__data-row",
                              isExpandable ? "audit-logs__row--expandable" : "",
                              isExpanded ? "audit-logs__row--expanded" : ""
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            onClick={() => {
                              if (isExpandable) {
                                toggleRow(log.id);
                              }
                            }}
                          >
                            <td className="audit-logs__toggle">
                              {isExpandable ? (
                                <i
                                  className={`fas fa-chevron-${
                                    isExpanded ? "down" : "right"
                                  }`}
                                ></i>
                              ) : null}
                            </td>
                            <td
                              className="audit-logs__cell"
                              title={String(serialNo)}
                            >
                              {serialNo}
                            </td>
                            <td
                              className="audit-logs__cell"
                              title={
                                log.created_at
                                  ? moment(log.created_at).format("LLL")
                                  : "—"
                              }
                            >
                              {log.created_at
                                ? moment(log.created_at).format(
                                    "MMM D, YYYY HH:mm"
                                  )
                                : "—"}
                            </td>
                            <td
                              className="audit-logs__cell"
                              title={log.actor_name || "—"}
                            >
                              {log.actor_name || "—"}
                            </td>
                            <td
                              className="audit-logs__cell"
                              title={log.actor_email || "—"}
                            >
                              {log.actor_email || "—"}
                            </td>
                            <td className="audit-logs__cell">
                              <span className="audit-logs__action-text">
                                {getAuditLogActionLabel(log.action)}
                              </span>
                            </td>
                            <td
                              className="audit-logs__cell audit-logs__cell--target"
                              {...getTooltipProps(target)}
                            >
                              <span className="audit-logs__cell-clamp">{target}</span>
                            </td>
                            <td
                              className="audit-logs__cell audit-logs__cell--details"
                              {...getTooltipProps(detailsPreview.summary)}
                            >
                              <span className="audit-logs__cell-clamp">
                                {detailsPreview.summary}
                              </span>
                            </td>
                          </tr>
                          {isExpanded ? (
                            <tr className="audit-logs__expanded-row">
                              <td colSpan="8">
                                <div className="audit-logs__changes">
                                  <div className="audit-logs__changes-header">
                                    <div className="audit-logs__changes-title">
                                      Changed Fields ({changes.length})
                                    </div>
                                  </div>
                                  <div className="audit-logs__changes-table-wrapper">
                                    <table className="table audit-logs__changes-table">
                                      <thead>
                                        <tr>
                                          <th>Field</th>
                                          <th>Previous</th>
                                          <th>Updated</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {changes.map((change, index) => (
                                          <tr key={`${log.id}-${index}`}>
                                            <td>{change.field}</td>
                                            <td className="audit-logs__value--previous">
                                              {change.from || "-"}
                                            </td>
                                            <td className="audit-logs__value--updated">
                                              {change.to || "-"}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
                <ReportsListingFooter
                  showLoader={isAuditLogsLoading}
                  apiMeta={auditLogsResponse?.meta}
                  page={parameters.page}
                  pageSize={parameters.limit}
                  onPageChange={page => {
                    setParameters({
                      ...parameters,
                      page
                    });
                  }}
                  onPageSizeChange={limit => {
                    setParameters({
                      ...parameters,
                      page: 1,
                      limit
                    });
                  }}
                />
              </div>
            )}
          </Loading>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default AuditLogs;
